import React, { useEffect, useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { noop } from 'lodash';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { LocaleService, WindowProxy } from '@sm360/widget-commons-core-services';
import { Vehicle, PurchaseMethodsV25, Quote } from '@sm360/widget-commons-vehicle';
import {
    ShiftDigitalService,
    AnalyticsService,
    TRACKING_TAGS as TAGS,
    ADOBE_TAGS,
    AdobeService,
} from '@sm360/widget-commons-general';

import PaymentCalculatorDisplay from 'payment-calculator-v25/components/PaymentCalculatorDisplay/PaymentCalculatorDisplay.jsx';
import * as Actions from 'payment-calculator-v25/actions/paymentCalculatorActions.js';
import 'payment-calculator-v25/components/PaymentCalculator/paymentCalculator.scss';

const PaymentCalculator = ({
    quote,
    hasSDInitialClickSent,
    onSetSDInitialClickEvent,
    onOpen,
    onClose,
    tradeInVehicle,
    actions,
    hasCustomButton,
    purchaseMethods,
    config,
    hasAdobeTagging,
    vehicle,
    displayCreditRate,
}) => {
    const [isPanelOpen, setPanelOpen] = useState(false);
    const $window = WindowProxy.get();
    const wrapperRef = useRef(null);
    const { ApiFetchStatusConstant } = PurchaseMethodsV25;
    /**
     * To get payment calculator data from paymentOptions endpoint
     * Get data once transaction token available
     */
    useEffect(
        () => {
            if (
                purchaseMethods.transactionToken !== '' &&
                purchaseMethods.paymentOptionsFetchStatus === ApiFetchStatusConstant.IDLE
            ) {
                actions.loadPurchaseMethods();
            }
        },
        [purchaseMethods.transactionToken]
    );

    useEffect(
        () => {
            if ($window) {
                $window.document.body.classList.toggle('PaymentCalculator--open', isPanelOpen);
            }
        },
        [isPanelOpen]
    );

    const handleCalculatorPanelClose = () => {
        onClose();
        // eslint-disable-next-line no-use-before-define
        $window.document.removeEventListener('click', handleOutsideClick, true);
        setPanelOpen(false);
    };

    const handleOutsideClick = useCallback((e) => {
        if (!wrapperRef?.current.contains(e.target)) {
            handleCalculatorPanelClose();
        }
    }, []);

    const handleClick = () => {
        if ($window) {
            // Only send initial click event once on page DR tool interaction
            if (!hasSDInitialClickSent) onSetSDInitialClickEvent();
            if (!isPanelOpen) {
                AnalyticsService.ga4Trigger(TAGS.PAYMENT_CALCULATOR_CTA(quote.vehicle.model.name, quote.vehicle.year));
                onOpen();
                $window.document.addEventListener('click', handleOutsideClick, true);
                if (quote)
                    ShiftDigitalService.updateDrCalculatorShown(
                        quote.id,
                        quote.vehicle,
                        quote.vehicle.purchaseMethods.getSelectedPurchaseMethodValue()
                    );
            } else {
                if (hasAdobeTagging) {
                    AdobeService.eventTrigger(
                        ADOBE_TAGS.VDP_PAYMENT_ESTIMATOR(
                            {
                                vehicleCondition: quote?.vehicle?.vehicleCondition,
                                make: quote?.vehicle?.make?.name,
                                model: quote?.vehicle?.model?.name,
                                year: quote?.vehicle?.year,
                                vehicleCategory: quote?.vehicle?.bodyStyle?.name,
                            },
                            vehicle?.commercialVehicle
                        ),
                        'payment-estimator-onclick'
                    );
                }
                onClose();
                $window.document.removeEventListener('click', handleOutsideClick, true);

                if (quote)
                    ShiftDigitalService.updateDrCalculatorFinish(
                        quote.id,
                        quote.vehicle,
                        quote.vehicle.purchaseMethods.getSelectedPurchaseMethodValue()
                    );
            }

            if (isPanelOpen && tradeInVehicle && tradeInVehicle.checkIsUpdatable()) {
                actions.saveTradeInVehicle(tradeInVehicle);
            }
            setPanelOpen(!isPanelOpen);
        }
    };

    const frequenciesPanelClasses = ['frequencies-panel-wrapper', 'sr-paymentCalculator-Panelbackground'];
    if (isPanelOpen) {
        frequenciesPanelClasses.push('is-open');
    }
    const setPaymentLabel = !isPanelOpen ? 'PAYMENT_CALCULATOR_V25.SET_PAYMENT' : 'PAYMENT_CALCULATOR_V25.SAVE_PAYMENT';
    const iconPanel = !isPanelOpen ? 'icon-calculatrice' : 'icon-Crochet';

    const customButton = hasCustomButton && (
        <a onClick={handleClick} className="sr-button-2 is-fluid vehicle-cta-btn">
            <icon className={iconPanel} />
            {LocaleService.t(setPaymentLabel)}
        </a>
    );

    const wrapperClasses = ['payment-calculator-wrapper'];
    if (isPanelOpen) {
        wrapperClasses.push('is-open');
    }

    return (
        <div className={wrapperClasses.join(' ')} ref={wrapperRef}>
            {customButton}
            <PaymentCalculatorDisplay
                purchaseMethods={purchaseMethods}
                panelFrequenciesClassNames={frequenciesPanelClasses}
                handleFrequenciesPanel={handleClick}
                frequenciesPanelOpen={isPanelOpen}
                hasCustomButton={hasCustomButton}
                hasAdobeTagging={hasAdobeTagging}
                config={config}
                vehicle={vehicle}
                displayCreditRate={displayCreditRate}
            />
        </div>
    );
};

PaymentCalculator.defaultProps = {
    purchaseMethods: new PurchaseMethodsV25.PurchaseMethodsModelV25(),
    hasCustomButton: false,
    hasSDInitialClickSent: false,
    tradeInVehicle: null,
    onSetSDInitialClickEvent: noop,
    onClose: noop,
    onOpen: noop,
    hasAdobeTagging: false,
    vehicle: new Vehicle.VehicleModelV25(),
    config: {},
    displayCreditRate: false,
};

PaymentCalculator.propTypes = {
    purchaseMethods: PropTypes.instanceOf(PurchaseMethodsV25.PurchaseMethodsModelV25),
    onClose: PropTypes.func,
    onOpen: PropTypes.func,
    hasCustomButton: PropTypes.bool,
    tradeInVehicle: PropTypes.instanceOf(Vehicle.TradeInVehicleModel),
    hasSDInitialClickSent: PropTypes.bool,
    onSetSDInitialClickEvent: PropTypes.func,
    actions: PropTypes.shape({
        saveTradeInVehicle: PropTypes.func.isRequired,
        selectPurchaseMethod: PropTypes.func.isRequired,
    }),
    quote: PropTypes.instanceOf(Quote.QuoteModelV25).isRequired,
    hasAdobeTagging: PropTypes.bool,
    vehicle: PropTypes.instanceOf(Vehicle.VehicleModelV25),
    config: PropTypes.object.isRequired,
    displayCreditRate: PropTypes.bool,
};

function mapStateToProps(state) {
    return {
        tradeInVehicle: state.tradeInVehicle,
        purchaseMethods: state.purchaseMethodsV25,
        quote: state.quoteV25,
    };
}

function mapDispatchToProps(dispatch) {
    return {
        actions: bindActionCreators(Actions, dispatch),
    };
}

PaymentCalculator.displayName = 'PaymentCalculator';

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(PaymentCalculator);
export { PaymentCalculator };
